/** Resolve the app's automatic density from the canonical CSS breakpoint. */
export function resolveDensity(
  preference: "comfortable" | "compact" | "auto",
  width: number,
  autoCompactBelowWidth: number,
) {
  return preference === "auto"
    ? width < autoCompactBelowWidth
      ? "compact"
      : "comfortable"
    : preference;
}

/** MDUI's legacy RGB channels are a projection of computed token colours. */
export function colourTriplet(colour: string): string {
  const hex = colour.trim().match(/^#([0-9a-f]{6})$/i);
  if (hex)
    return [0, 2, 4]
      .map((offset) => parseInt(hex[1].slice(offset, offset + 2), 16))
      .join(", ");
  const rgb = colour
    .trim()
    .match(/^rgba?\(\s*(\d+)\s*[, ]\s*(\d+)\s*[, ]\s*(\d+)/);
  if (rgb) return rgb.slice(1, 4).join(", ");
  throw new Error(`Invalid computed token colour: ${colour}`);
}

export function applyThemeAttributes(
  theme: "dark" | "light",
  highContrast: boolean,
  density: "comfortable" | "compact" | "auto",
) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.contrast = highContrast ? "more" : "normal";
  const breakpoint = parseFloat(
    getComputedStyle(root).getPropertyValue(
      "--mutiny-platform-auto-compact-below-width",
    ),
  );
  root.dataset.density = resolveDensity(density, window.innerWidth, breakpoint);
  // MDUI's own system-theme classes must not override the explicit preference.
  document.body.classList.toggle("mdui-theme-dark", theme === "dark");
  document.body.classList.toggle("mdui-theme-light", theme === "light");
  const computed = getComputedStyle(root);
  for (let i = 0; i < computed.length; i++) {
    const property = computed[i];
    if (
      property.startsWith("--mdui-color-") &&
      !property.endsWith("-dark") &&
      !property.endsWith("-light")
    ) {
      document.body.style.setProperty(
        property,
        colourTriplet(computed.getPropertyValue(property)),
      );
    }
  }
}

/** CSS and System defaults are installed synchronously before the first app mount. */
export function initialiseTheme() {
  applyThemeAttributes(
    window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light",
    false,
    "auto",
  );
}
